import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Pause,
  XCircle,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  Filter
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { BulkJob, BulkJobStatus } from '../../lib/jobs/types';
import { JobDetailModal } from '../admin/JobDetailModal';

interface TaskNotificationCenterProps {
  align?: 'left' | 'right';
}

export default function TaskNotificationCenter({ align = 'right' }: TaskNotificationCenterProps) {
  const [jobs, setJobs] = useState<BulkJob[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'failed'>('all');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<BulkJob | null>(null);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePos = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      top: rect.bottom + 8,
      left: align === 'right' ? Math.max(8, rect.right - 360) : Math.max(8, rect.left),
    });
  }, [align]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await safeFetch('/api/admin/bulk-jobs?limit=50');
      if (res && res.jobs) {
        setJobs(res.jobs);
      }
    } catch {
      // Non-admin or unauthenticated call fails gracefully
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, open ? 4000 : 15000);
    const handleJobCreated = () => {
      fetchJobs();
    };
    window.addEventListener('bulk-job-created', handleJobCreated);
    return () => {
      clearInterval(interval);
      window.removeEventListener('bulk-job-created', handleJobCreated);
    };
  }, [fetchJobs, open]);

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    document.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      document.removeEventListener('scroll', updatePos, true);
    };
  }, [open, updatePos]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideButton = panelRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!insideButton && !insideDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const activeJobs = jobs.filter((j) => j.status === 'processing' || j.status === 'queued');
  const activeCount = activeJobs.length;

  const filteredJobs = jobs.filter((j) => {
    if (statusFilter === 'active') return j.status === 'processing' || j.status === 'queued' || j.status === 'paused';
    if (statusFilter === 'completed') return j.status === 'completed' || j.status === 'completed_with_errors';
    if (statusFilter === 'failed') return j.status === 'failed' || j.status === 'cancelled';
    return true;
  });

  const getStatusBadge = (status: BulkJobStatus) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="w-3 h-3" /> Completed
          </span>
        );
      case 'completed_with_errors':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            <AlertTriangle className="w-3 h-3" /> Partial
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 animate-pulse">
            <Loader2 className="w-3 h-3 animate-spin" /> In Progress
          </span>
        );
      case 'paused':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700">
            <Pause className="w-3 h-3" /> Paused
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">
            <XCircle className="w-3 h-3" /> Failed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">
            <XCircle className="w-3 h-3" /> Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800">
            <Clock className="w-3 h-3" /> Queued
          </span>
        );
    }
  };

  const handleOpenJobDetail = (job: BulkJob) => {
    setSelectedJob(job);
    setSelectedJobId(job.id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Clock Icon Trigger Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          fetchJobs();
          setOpen((o) => !o);
        }}
        className="relative p-2 rounded-lg text-slate-500 hover:text-brand hover:bg-brand-active transition-all cursor-pointer"
        title="Backend Tasks & Background Operations"
        aria-label="Backend Tasks"
      >
        <Clock className={`h-4 w-4 ${activeCount > 0 ? 'text-blue-600 animate-pulse' : ''}`} />
        {activeCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center shadow-xs">
            {activeCount > 99 ? '99+' : activeCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown */}
      {open && pos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="dropdown-portal w-90 max-w-[90vw] bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ top: pos.top, left: pos.left, zIndex: 'var(--z-dropdown)' }}
        >
          {/* Popover Top Bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 text-slate-900">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-brand-primary" />
              <span className="text-xs font-black uppercase tracking-wider">Backend Tasks</span>
              {activeCount > 0 && (
                <span className="px-2 py-0.5 bg-brand-primary text-white text-[10px] font-bold rounded-full">
                  {activeCount} Running
                </span>
              )}
            </div>
            <button
              onClick={fetchJobs}
              className="p-1 hover:bg-slate-200/60 text-slate-500 hover:text-slate-700 rounded transition-colors cursor-pointer"
              title="Refresh Tasks"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Filter Bar */}
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                  statusFilter === 'all' ? 'bg-brand-primary text-white' : 'text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                All ({jobs.length})
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                  statusFilter === 'active' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                Active ({jobs.filter((j) => j.status === 'processing' || j.status === 'queued' || j.status === 'paused').length})
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                  statusFilter === 'completed' ? 'bg-success text-white' : 'text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                Completed ({jobs.filter((j) => j.status === 'completed' || j.status === 'completed_with_errors').length})
              </button>
              <button
                onClick={() => setStatusFilter('failed')}
                className={`px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                  statusFilter === 'failed' ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                Failed
              </button>
            </div>
          </div>

          {/* Task Items List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {filteredJobs.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">
                No backend tasks found
              </div>
            ) : (
              filteredJobs.map((j) => (
                <div
                  key={j.id}
                  onClick={() => handleOpenJobDetail(j)}
                  className="p-3.5 hover:bg-slate-50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-xs text-slate-900 capitalize truncate">
                          {j.type.replace(/_/g, ' ')}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-[10px] font-mono text-slate-400 mt-0.5 truncate">{j.id}</p>
                    </div>
                    {getStatusBadge(j.status)}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-2.5">
                    <div className="flex justify-between text-[10px] text-slate-600 mb-1 font-medium">
                      <span>Progress</span>
                      <span>
                        {j.progressPercent}% ({j.completedItems + j.failedItems + j.skippedItems}/{j.totalItems})
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          j.status === 'completed'
                            ? 'bg-emerald-500'
                            : j.status === 'failed'
                            ? 'bg-rose-500'
                            : j.status === 'paused'
                            ? 'bg-slate-400'
                            : 'bg-blue-600'
                        }`}
                        style={{ width: `${j.progressPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
                    <span>By: {j.createdByName || j.createdByEmail || 'Admin'}</span>
                    <span>{new Date(j.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Popover Footer */}
          <div className="p-2.5 border-t border-slate-100 bg-slate-50 text-center">
            <a
              href="/admin?tab=bulk-jobs"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
            >
              Open Bulk Operations Platform <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>,
        document.body
      )}

      {/* Selected Job Detail Popup Modal */}
      {selectedJobId && (
        <JobDetailModal
          jobId={selectedJobId}
          initialJob={selectedJob}
          onClose={() => {
            setSelectedJobId(null);
            setSelectedJob(null);
          }}
          onJobUpdated={fetchJobs}
        />
      )}
    </div>
  );
}
